import React from "react";
import { Divider, Typography } from "@material-ui/core";
import InfoIcon from "@material-ui/icons/Info";
import { useTranslation } from "react-i18next";
import Utils from "../../../helpers/Utils";
import { isConsultationPrice, areAllConsultationPrices } from "../../../utils/checkout/priceValidation";
import useStyles from "./styles";

const PaymentSummary = ({
	checkoutInformation,
	payload,
	couponValue,
	totalPricingType,
	currency,
	offerAccepted, 
	laterPayment, 
	postPayment,
	sessionsAccepted,
}) => {
	const classes = useStyles();
	const { t } = useTranslation();

	// Exibe apenas linhas com valor > 0 e que não sejam "sob consulta" ou "Starts at"
	const parseAmountFromPriceString = (priceString) => {
		if (!priceString || isConsultationPrice(priceString)) return null;
		// Captura o último bloco numérico (geralmente o valor monetário)
		const matches = String(priceString).match(/[\d.,]+/g);
		if (!matches || matches.length === 0) return null;
		let numericPart = matches[matches.length - 1];
		// Normaliza separadores de milhar/decimal para ponto decimal
		if (numericPart.includes(",") && numericPart.includes(".")) {
			numericPart = numericPart.replace(/\./g, "").replace(",", ".");
		} else if (numericPart.includes(",")) {
			numericPart = numericPart.replace(/\./g, "").replace(",", ".");
		} else {
			numericPart = numericPart.replace(/,/g, "");
		}
		const amount = parseFloat(numericPart);
		return Number.isNaN(amount) ? null : amount;
	};

	const shouldShowRow = (priceString) => {
		const amount = parseAmountFromPriceString(priceString);
		return amount !== null && amount > 0;
	};

	// Verifica se há mais de um serviço
	const hasMultipleServices = payload?.services?.length > 1;

	// Verifica se há apenas um serviço e não há pagamento antecipado
	const hasOnlyOneService = payload?.services?.length === 1;
	const hasNoAnticipationPayment =
		checkoutInformation?.anticipation_payment === 0 ||
		(!!checkoutInformation?.anticipation_payment_percentage && !offerAccepted);

	const allServicesFree = payload?.services?.length > 0 && 
		payload.services.every((service) => service?.professional && Utils.freePricingTypeNames.includes(service.professional.pricingType));

	const shouldShowLocalPaymentOnly = hasOnlyOneService && hasNoAnticipationPayment && !allServicesFree;

	const totalPrice = Utils.getTotalPrice(
		checkoutInformation,
		payload?.services?.[0]?.professional?.pricingType || "fixed",
		currency,
		t
	);

	const discountPrice = Utils.getDiscountPrice(
		checkoutInformation,
		payload?.services?.[0]?.professional?.pricingType || "fixed",
		currency,
		couponValue,
		t
	);

	const balancePrice = Utils.getBalancePrice(
		checkoutInformation,
		totalPricingType || "fixed",
		currency,
		couponValue,
		t
	);

	const anticipationPrice =
		checkoutInformation?.anticipation_payment > 0
			? Utils.getAntecipatedPrice(
					checkoutInformation,
					payload?.services?.[0]?.professional?.pricingType || "fixed",
					currency,
					t,
					offerAccepted
			  )
			: null;

	const hasActualDiscount = !!(couponValue?.coupon || checkoutInformation?.total_discounts > 0);

	const allPrices = [totalPrice, discountPrice, balancePrice];
	if (anticipationPrice) allPrices.push(anticipationPrice);

	const allAreConsultation = areAllConsultationPrices(allPrices);

	if (allAreConsultation && !laterPayment) {
		return null;
	}

	if (laterPayment) {
		// Verifica se há request_payment > 0 para mostrar valores específicos
		const hasRequestPayment = postPayment?.checkoutInformation?.request_payment > 0;
		
		if (hasRequestPayment) {
			// Calcula os valores
			const totalValueServices = postPayment?.amount || 0;
			const requestPayment = postPayment?.amount || 0;
			const amountToBePaid = totalValueServices - requestPayment;
			
			// Formata os valores
			const formatCurrency = (value) => {
				return Utils.formatMoney((value / 100), currency);
			};

			return (
				<div className={classes.paymentSummary}>
					<div className={classes.paymentTable}>
						{/* Pagamento antecipado */}
						<div className={classes.paymentRow}>
							<div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
								<InfoIcon style={{ fontSize: "16px", color: "#666" }} />
								<Typography className={classes.paymentLabel}>{t("Payment advance")}</Typography>
							</div>
							<Typography className={classes.paymentValue}>
								{formatCurrency(requestPayment)}
							</Typography>
						</div>
						
						{/* Valor a ser pago */}
						{amountToBePaid > 0 && (
							<div className={classes.paymentRow}>
								<div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
									<InfoIcon style={{ fontSize: "16px", color: "#666" }} />
									<Typography className={classes.paymentLabel}>{t("Amount to be paid")}</Typography>
								</div>
								<Typography className={classes.paymentValue}>
									{formatCurrency(amountToBePaid)}
								</Typography>
							</div>
						)}
					</div>
				</div>
			);
		}

		// Caso não tenha request_payment, mostra o valor total dos serviços
		const totalValueServices = postPayment?.amount || 0;
		
		// Formata o valor
		const formatCurrency = (value) => {
			return Utils.formatMoney((value / 100), currency);
		};

		return (
			<div className={classes.paymentSummary}>
				<div className={classes.paymentTable}>
					<div className={classes.paymentRow}>
						<div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
							<InfoIcon style={{ fontSize: "16px", color: "#666" }} />
							<Typography className={classes.paymentLabel}>{t("Pay now")}</Typography>
						</div>
						<Typography className={classes.paymentValue}>
							{formatCurrency(totalValueServices)}
						</Typography>
					</div>
				</div>
			</div>
		);
	}

	// Se há apenas um serviço e não há pagamento antecipado, mostra apenas "Pagamento no local"
	if (shouldShowLocalPaymentOnly) {
		return (
			<div className={classes.paymentSummary}>
				<div className={classes.paymentTable}>
					<div className={classes.paymentRow}>
						<div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
							<InfoIcon style={{ fontSize: "16px", color: "#666" }} />
							<Typography className={classes.paymentLabel}>{t("pay in person")}</Typography>
						</div>
					</div>
				</div>
			</div>
		);
	}

	// Se há múltiplos serviços, mostra o total e soma dos serviços
	if (hasMultipleServices) {
		return (
			<>
				<div className={classes.paymentSummary}>
					<div className={classes.paymentTable}>
						{shouldShowRow(totalPrice) && (
							<div className={classes.paymentRow}>
								<Typography className={classes.paymentLabel} style={{ fontWeight: "bold" }}>
									{t("Total")}
								</Typography>
								<Typography className={classes.paymentValue}>{totalPrice}</Typography>
							</div>
						)}
						{hasActualDiscount && shouldShowRow(discountPrice) && (
							<div className={classes.paymentRow}>
								<Typography className={classes.paymentLabel}>{t("Discount")}</Typography>
								<Typography className={classes.paymentValue}>{discountPrice}</Typography>
							</div>
						)}

						{checkoutInformation?.anticipation_payment > 0 && shouldShowRow(anticipationPrice) && (
							<div className={classes.paymentRow}>
								<Typography className={classes.paymentLabel}>{t("Payment advance")}</Typography>
								<Typography className={classes.paymentValue} style={{ fontWeight: "normal" }}>
									{anticipationPrice}
								</Typography>
							</div>
						)}

						{shouldShowRow(balancePrice) && !hasNoAnticipationPayment && !offerAccepted && (
							<div className={classes.paymentRow}>
								<Typography className={classes.paymentLabel}>
									{checkoutInformation?.pricing_type ? t("Balance payable from") : t("Pay at location")}
								</Typography>
								<Typography className={classes.paymentValue} style={{ fontWeight: "normal" }}>
									{balancePrice}
								</Typography>
							</div>
						)}
						{!offerAccepted && (
							<>
								{hasNoAnticipationPayment && (
									<div className={classes.paymentRow}>
										<div style={{ display: "flex", alignItems: "center", gap: "8px", width: "100%" }}>
											<InfoIcon style={{ fontSize: "16px", color: "#666" }} />
											<Typography className={classes.paymentLabel} style={{ width: "100%" }}>
												{t("pay in person")}
											</Typography>
										</div>
									</div>
								)}
							</>
						)}
					</div>
				</div>
			</>
		);
	}

	// Caso padrão para um serviço com pagamento antecipado
	return (
		<>
			<div className={classes.paymentSummary}>
				<div className={classes.paymentTable}>
					{hasActualDiscount && shouldShowRow(discountPrice) && (
						<div className={classes.paymentRow}>
							<Typography className={classes.paymentLabel}>{t("Discount")}</Typography>
							<Typography className={classes.paymentValue}>{discountPrice}</Typography>
						</div>
					)}

					{checkoutInformation?.anticipation_payment > 0 && shouldShowRow(anticipationPrice) && (
						<div className={classes.paymentRow}>
							<Typography className={classes.paymentLabel}>{t("Payment advance")}</Typography>
							<Typography className={classes.paymentValue} style={{ fontWeight: "normal" }}>
								{anticipationPrice}
							</Typography>
						</div>
					)}

					{shouldShowRow(balancePrice) && !offerAccepted && (
						<div className={classes.paymentRow}>
							<Typography className={classes.paymentLabel}>
								{checkoutInformation?.pricing_type ? t("Balance payable from") : t("Pay at location")}
							</Typography>
							<Typography className={classes.paymentValue} style={{ fontWeight: "normal" }}>
								{balancePrice}
							</Typography>
						</div>
					)}
				</div>
			</div>
		</>
	);
};

export default PaymentSummary;
